import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import LazyChunkBoundary from "@/components/LazyChunkBoundary";

const AssinarContrato = lazy(() => import("@/pages/AssinarContrato"));
const DesignPublic = lazy(() => import("@/pages/DesignPublic"));
const MessengerOAuthCallback = lazy(() => import("@/pages/MessengerOAuthCallback"));
const SystemRoutes = lazy(() => import("@/components/SystemRoutes"));
const SiteLayout = lazy(() => import("@/pages/site/SiteLayout"));
const SiteHome = lazy(() => import("@/pages/site/SiteHome"));
const PontoMobile = lazy(() => import("@/pages/public/PontoMobile"));
const SiteServicos = lazy(() => import("@/pages/site/SiteServicos"));
const SiteGaleria = lazy(() => import("@/pages/site/SiteGaleria"));
const SiteSobre = lazy(() => import("@/pages/site/SiteSobre"));
const SiteAgendar = lazy(() => import("@/pages/site/SiteAgendar"));
const SiteContato = lazy(() => import("@/pages/site/SiteContato"));
const SiteBlog = lazy(() => import("@/pages/site/SiteBlog"));
const SiteBlogPost = lazy(() => import("@/pages/site/SiteBlogPost"));

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const queryClient = new QueryClient();

type HostMode = 'site' | 'system' | 'legacy';
function getHostMode(): HostMode {
  if (typeof window === 'undefined') return 'legacy';
  const host = window.location.hostname.toLowerCase();
  if (host === 'www.jftapecaria.store' || host === 'jftapecaria.store') return 'site';
  if (host === 'jftapecariaautomotiva.lovable.app') return 'system';
  return 'legacy';
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AppShell />
    </TooltipProvider>
  </QueryClientProvider>
);

function PublicSiteRoutes() {
  return (
    <Routes>
      <Route path="/assinar/:quoteNumber" element={<AssinarContrato />} />
      <Route path="/design/:token" element={<DesignPublic />} />
      <Route path="/" element={<SiteLayout />}>
        <Route index element={<SiteHome />} />
        <Route path="servicos" element={<SiteServicos />} />
        <Route path="galeria" element={<SiteGaleria />} />
        <Route path="sobre" element={<SiteSobre />} />
        <Route path="agendar" element={<SiteAgendar />} />
        <Route path="contato" element={<SiteContato />} />
        <Route path="blog" element={<SiteBlog />} />
        <Route path="blog/:slug" element={<SiteBlogPost />} />
      </Route>
      <Route path="/site" element={<SiteLayout />}>
        <Route index element={<SiteHome />} />
        <Route path="servicos" element={<SiteServicos />} />
        <Route path="galeria" element={<SiteGaleria />} />
        <Route path="sobre" element={<SiteSobre />} />
        <Route path="agendar" element={<SiteAgendar />} />
        <Route path="contato" element={<SiteContato />} />
        <Route path="blog" element={<SiteBlog />} />
        <Route path="blog/:slug" element={<SiteBlogPost />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppShell() {
  const mode = getHostMode();
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';

  if (mode === 'site' && typeof window !== 'undefined') {
    if (path.startsWith('/sistema')) {
      return (
        <BrowserRouter basename="/sistema">
          <LazyChunkBoundary>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/assinar/:quoteNumber" element={<AssinarContrato />} />
                <Route path="/design/:token" element={<DesignPublic />} />
                <Route path="/messenger/oauth-callback" element={<MessengerOAuthCallback />} />
                <Route path="/*" element={<SystemRoutes />} />
              </Routes>
            </Suspense>
          </LazyChunkBoundary>
        </BrowserRouter>
      );
    }
    if (path.startsWith('/ponto')) {
      return (
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/ponto" element={<PontoMobile />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      );
    }
    return (
      <BrowserRouter>
        <LazyChunkBoundary>
          <Suspense fallback={<PageFallback />}>
            <PublicSiteRoutes />
          </Suspense>
        </LazyChunkBoundary>
      </BrowserRouter>
    );
  }

  if (mode === 'system') {
    return (
      <BrowserRouter>
        <LazyChunkBoundary>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/assinar/:quoteNumber" element={<AssinarContrato />} />
              <Route path="/design/:token" element={<DesignPublic />} />
              <Route path="/ponto" element={<PontoMobile />} />
              <Route path="/messenger/oauth-callback" element={<MessengerOAuthCallback />} />
              <Route path="/*" element={<SystemRoutes />} />
            </Routes>
          </Suspense>
        </LazyChunkBoundary>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <LazyChunkBoundary>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/assinar/:quoteNumber" element={<AssinarContrato />} />
            <Route path="/design/:token" element={<DesignPublic />} />
            <Route path="/ponto" element={<PontoMobile />} />
            <Route path="/messenger/oauth-callback" element={<MessengerOAuthCallback />} />
            <Route path="/site/*" element={<PublicSiteRoutes />} />
            <Route path="/*" element={<SystemRoutes />} />
          </Routes>
        </Suspense>
      </LazyChunkBoundary>
    </BrowserRouter>
  );
}

export default App;
